/**
 * Proaktív felkérés (teaser buborék) trigger logika.
 *
 * Tudatosan cookie- és localStorage-mentes: az „ebben a látogatásban már láttam" jelzés
 * kizárólag sessionStorage, ami a fül bezárásakor eltűnik. Ne cseréld localStorage-ra —
 * ez jogi (ePrivacy) megfontolás, nem technikai kényelmi kérdés.
 */

const SESSION_KEY_PREFIX = 'chatt_proactive_shown_';

/** Az exit-intent csak ennyi idő után élesedik, hogy a betöltés közbeni kurzormozgás ne triggerelje. */
const EXIT_INTENT_ARM_DELAY_MS = 5000;

const MIN_DELAY_SECONDS = 30;
const MAX_DELAY_SECONDS = 300;

export function proactiveSessionKey(botKey) {
    return `${SESSION_KEY_PREFIX}${botKey}`;
}

/**
 * A szerver már normalizálva küldi az értékeket — ez csak védőháló hibás / régi payloadra.
 */
export function normalizeProactiveConfig(raw) {
    if (! raw || typeof raw !== 'object') {
        return null;
    }

    const message = typeof raw.message === 'string' ? raw.message.trim() : '';

    if (message === '') {
        return null;
    }

    const trigger = ['delay', 'exit_intent', 'both'].includes(raw.trigger) ? raw.trigger : 'delay';
    const rawDelay = Number.parseInt(raw.delay_seconds, 10);
    const delaySeconds = Number.isFinite(rawDelay)
        ? Math.min(Math.max(rawDelay, MIN_DELAY_SECONDS), MAX_DELAY_SECONDS)
        : MIN_DELAY_SECONDS;

    return {
        trigger,
        delaySeconds,
        message,
        pathPattern: typeof raw.path_pattern === 'string' && raw.path_pattern.trim() !== ''
            ? raw.path_pattern.trim()
            : null,
    };
}

/**
 * Üres minta = minden oldal. A `*` bármennyi karaktert helyettesít, a vizsgálat
 * kis/nagybetű-független, és a látogató útvonalára (pathname) fut. A lezáró `/`
 * elhagyható, hogy a `/arak` és `/arak/` ugyanazt jelentse.
 */
export function matchesCurrentPath(pattern, pathname = window.location?.pathname ?? '/') {
    if (! pattern) {
        return true;
    }

    const escaped = pattern
        .replace(/[.+?^${}()|[\]\\]/g, '\\$&')
        .replace(/\*/g, '.*');

    try {
        return new RegExp(`^${escaped}/?$`, 'i').test(pathname);
    } catch {
        return false;
    }
}

export function wasShownThisSession(botKey) {
    try {
        return window.sessionStorage?.getItem(proactiveSessionKey(botKey)) === '1';
    } catch {
        // Privát mód / letiltott tárolás: inkább ne jelenjen meg többször, mint hogy hibázzunk.
        return false;
    }
}

export function markShownThisSession(botKey) {
    try {
        window.sessionStorage?.setItem(proactiveSessionKey(botKey), '1');
    } catch {
        // Némán elnyeljük: a felkérés megjelenése ne dőljön el a tárolás elérhetőségén.
    }
}

/**
 * Egy felkérés / munkamenet: időzített és/vagy exit-intent trigger felfegyverzése.
 *
 * @param {{ botKey: string, config: object|null, onShow: () => void }} options
 * @returns {() => void} leállító (timer + listener eltakarítás)
 */
export function startProactiveInvitation({ botKey, config, onShow }) {
    const normalized = normalizeProactiveConfig(config);
    const noop = () => {};

    if (normalized === null || ! matchesCurrentPath(normalized.pathPattern) || wasShownThisSession(botKey)) {
        return noop;
    }

    let delayTimer = null;
    let armTimer = null;
    let exitIntentHandler = null;
    let finished = false;

    const stop = () => {
        if (delayTimer !== null) {
            clearTimeout(delayTimer);
            delayTimer = null;
        }

        if (armTimer !== null) {
            clearTimeout(armTimer);
            armTimer = null;
        }

        if (exitIntentHandler !== null) {
            document.removeEventListener('mouseleave', exitIntentHandler);
            exitIntentHandler = null;
        }
    };

    const fire = () => {
        if (finished) {
            return;
        }

        finished = true;
        stop();
        markShownThisSession(botKey);
        onShow();
    };

    if (normalized.trigger !== 'exit_intent') {
        delayTimer = setTimeout(fire, normalized.delaySeconds * 1000);
    }

    if (normalized.trigger !== 'delay') {
        armTimer = setTimeout(() => {
            armTimer = null;
            exitIntentHandler = (event) => {
                if (event.clientY <= 0) {
                    fire();
                }
            };
            document.addEventListener('mouseleave', exitIntentHandler);
        }, EXIT_INTENT_ARM_DELAY_MS);
    }

    return stop;
}
